import { Question } from '@amzn/awscat-portfolio-assessment-types';
import { Box, Button, Container, FormField, Header, Input, Select, SpaceBetween } from '@amzn/awsui-components-react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { useEffect, useState } from 'react';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../../common/Localization';
import { deepCopy } from '../../../common/util';
import DataCollectionEditQuestion from './DataCollectionEditQuestion';

interface Props {
    question: Question;
    addQuestion: (question: Question) => Promise<void>;
    updateQuestion: (currentQuestion: Question, updatedQuestion: Question) => Promise<void>;
    deleteQuestion: (question: Question) => Promise<void>;
    activeTab: string;
    propertyOptions: OptionDefinition[];
}

function DataCollectionViewEditQuestion({ question, addQuestion, updateQuestion, deleteQuestion, activeTab, propertyOptions }: Props) {

    const [ editMode, setEditMode ] = useState<boolean>(question.questionId === undefined);
    const [ loadingSave, setLoadingSave ] = useState(false);
    const [ loadingDelete, setLoadingDelete ] = useState(false);
    const [ questionToEdit, setQuestionToEdit ] = useState<Question>(question);
    const [ verifyDeleteQuestion, setVerifyDeleteQuestion ] = useState(false);
    const [ errorMsg, setErrorMsg ] = useState<string>();
    const isMounted = useIsMounted();

    useEffect(() => {
        setQuestionToEdit(question);
    }, [ question ]);

    const getSelectedAnswer = () => {
        return questionToEdit?.possibleAnswers?.find(answer => answer.value === question.hint) ?? null;
    };

    const saveQuestion = async () => {
        setErrorMsg(undefined);
        setLoadingSave(true);
        try {
            if (question.questionId) {
                await updateQuestion(question, questionToEdit);
            } else {
                await addQuestion(questionToEdit);
            }
            if (isMounted()) {
                setEditMode(false);
            }
        } catch (err) {
            if (isMounted()) {
                setErrorMsg((err as Error).message);
            }
        } finally {
            if (isMounted()) {
                setLoadingSave(false);
            }
        }
    };

    const confirmDelete = () => {
        setVerifyDeleteQuestion(true);
    };

    const onDeleteQuestionConfirmed = async () => {
        setLoadingDelete(true);
        try {
            await deleteQuestion(question);
        } catch (err) {
            if (isMounted()) {
                setErrorMsg((err as Error).message);
            }
        } finally {
            if (isMounted()) {
                setLoadingDelete(false);
                setVerifyDeleteQuestion(false);
            }
        }
    };

    const updateQuestionToEdit = (currentQuestion: Question, updatedQuestion: Question) => {
        setQuestionToEdit(deepCopy(updatedQuestion));
    };

    const cancel = () => {
        setEditMode(false);
        setErrorMsg(undefined);
        if (!question.questionId) {
            deleteQuestion(question);
        } else {
            setQuestionToEdit(deepCopy(question));
        }
    };

    const getHeaderActions = (): JSX.Element => {
        if (editMode) {
            return (
                <SpaceBetween size='xs' direction='horizontal'>
                    <Button onClick={saveQuestion} loading={loadingSave}>{constants.SAVE_BUTTON_TEXT}</Button>
                    <Button iconName='close' variant='icon' onClick={cancel}></Button>
                </SpaceBetween>
            );
        } else if (verifyDeleteQuestion) {
            return (
                <SpaceBetween size='xs' direction='horizontal'>
                    <Box display='inline'>{constants.DELETE_QUESTION}</Box>
                    <Button 
                        onClick={() => setVerifyDeleteQuestion(false)} 
                        disabled={loadingDelete}
                    >{constants.CANCEL_BUTTON_TEXT}</Button>
                    <Button
                        variant='primary'
                        onClick={onDeleteQuestionConfirmed} 
                        loading={loadingDelete}
                    >{constants.CONTINUE_BUTTON_TEXT}</Button>
                </SpaceBetween>
            );
        } else {
            return (
                <SpaceBetween size='xs' direction='horizontal'>
                    <Button iconName='edit' variant='icon' onClick={() => setEditMode(true)}></Button>
                    <Button iconName='close' variant='icon' onClick={confirmDelete}></Button>
                </SpaceBetween>
            );
        }
    };

    const header = (
        <Header
            variant='h2'
            actions={getHeaderActions()}
        >
            {questionToEdit.label} {<Box color='text-status-error' variant='span'>{errorMsg}</Box>}
        </Header>
    );

    const content = editMode ? (
        <DataCollectionEditQuestion
            question={questionToEdit}
            updateQuestion={updateQuestionToEdit}
            activeTab={activeTab}
            propertyOptions={propertyOptions}
        />
    ) : (
        <SpaceBetween size='s'>
            <FormField
                label={questionToEdit.questionText}
            >
                { 
                    questionToEdit.answerType === 'select' && (
                        <Select
                            options={questionToEdit.possibleAnswers}
                            selectedOption={getSelectedAnswer()}
                            disabled={true}
                        ></Select>
                    )
                }
                {
                    (questionToEdit.answerType === 'text' || questionToEdit.answerType === 'number') && (
                        <Input
                            value={questionToEdit.hint}
                            type={questionToEdit.answerType}
                            disabled={true}
                        />
                    )
                }
            </FormField>
        </SpaceBetween>
    );

    return (
        <Container
            header={header}
        >
            {content}
        </Container>
    );
}

export default DataCollectionViewEditQuestion;
